import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { InventoryBalance, Location } from "../../api/types";
import { useAuth } from "../../state/AuthContext";
import { InventoryStatisticsModal } from "../InventoryStatisticsModal";
import { LogWasteModal } from "../LogWasteModal";
import { ReceiveBatchHistoryModal } from "../ReceiveBatchHistoryModal";
import { ConsumablesModal } from "./ConsumablesModal";
import { InterLocationTransferModal } from "./InterLocationTransferModal";

/**
 * G-94 S4 — everything you can DO to one shop's stock, on the inventory page.
 *
 * These verbs lived on `LocationDashboardPage`, which this work order retires.
 * Retiring it by redirect alone would have deleted six working features, so
 * they move first and the redirect comes last: nothing is ever unreachable.
 *
 * Every item here is the one that was on the shop page, wired to the same
 * modal and the same role gate. What changed is where it lives and that it now
 * needs a shop to be CHOSEN — in All-shops mode this component is not rendered
 * at all, because none of these verbs has an honest answer to "at which shop?".
 *
 * G-132: the toolbar card became a menu — "More actions" in the page's floating
 * "Actions" menu opens
 * the `.inv-sheet` bottom sheet (the same shell the item card uses), and
 * choosing an item closes it and opens that modal. The modals live here so a
 * deep link (`?transferItem=`) can open one without the menu.
 */
export function InventoryShopActions({
  shop,
  rows,
  otherShops,
  open,
  onClose,
  onChanged,
  transferPreset,
  onTransferPresetDone,
}: {
  shop: Location;
  /** This shop's on-hand rows — what transfer and waste can act on. */
  rows: InventoryBalance[];
  /** Active shops other than this one, for a shop-to-shop transfer. */
  otherShops: Location[];
  /** The sheet itself (the page's "More actions" item owns this). */
  open: boolean;
  onClose: () => void;
  /** Any successful write: the page re-reads its balances. */
  onChanged: () => void;
  /** G-113 S2: a Shopify order's "Transfer now" deep link; opens the modal prefilled. */
  transferPreset?: { itemId?: string; toLocationId?: string; quantity?: string } | null;
  /** The modal closed (either way): the page drops the deep-link params. */
  onTransferPresetDone?: () => void;
}) {
  const { hasRole } = useAuth();
  const canOperateFlow = hasRole("OWNER", "MANAGER", "FLORIST");

  const [transferOpen, setTransferOpen] = useState(false);
  const closeTransfer = () => {
    setTransferOpen(false);
    if (transferPreset) onTransferPresetDone?.();
  };
  const [wasteModal, setWasteModal] = useState<
    { presetItemId?: string; presetItemName?: string } | null
  >(null);
  const [consumableOpen, setConsumableOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  /**
   * Carried over from the shop page unchanged: transfer and waste can only
   * offer what the shop actually holds, so an empty shelf disables them rather
   * than opening a modal with an empty picker.
   */
  const itemOptionsFromStock = useMemo(() => {
    const map = new Map<
      string,
      { item: InventoryBalance["item"]; zones: { zone: InventoryBalance["zone"]; qty: number }[] }
    >();
    for (const row of rows) {
      const qty = Number.parseFloat(row.qtyOnHand);
      if (!Number.isFinite(qty) || qty <= 0) continue;
      const entry = map.get(row.item.id) ?? { item: row.item, zones: [] };
      entry.zones.push({ zone: row.zone, qty });
      map.set(row.item.id, entry);
    }
    return map;
  }, [rows]);

  const nothingOnTheShelf = itemOptionsFromStock.size === 0;

  /** Close the menu, then open the thing that was chosen. */
  const choose = (action: () => void) => {
    onClose();
    action();
  };

  /**
   * A deep link may arrive after the stock moved: the item is no longer here
   * (or the shelf is empty). Then the link says so instead of opening a form
   * with an empty item picker — or, worse, nothing at all.
   */
  const presetItemGone =
    !!transferPreset?.itemId && !itemOptionsFromStock.has(transferPreset.itemId);
  useEffect(() => {
    if (transferPreset && !presetItemGone) setTransferOpen(true);
  }, [transferPreset, presetItemGone]);

  return (
    <>
      {transferPreset && presetItemGone ? (
        <div className="alert error inventory-shop-actions-notice">
          <span>
            {shop.name} no longer holds the item this transfer link asked for — it may have
            been moved or sold since the order was planned. Re-check the order.
          </span>
          <button type="button" className="button plain" onClick={() => onTransferPresetDone?.()}>
            Dismiss
          </button>
        </div>
      ) : null}

      {open ? (
        <div
          className="inv-sheet-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <div className="inv-sheet" role="dialog" aria-modal="true" aria-label={`More actions for ${shop.name}`}>
            <div className="inv-sheet-title">More actions</div>
            <div className="inv-sheet-sub">{shop.name}</div>
            <button
              type="button"
              className="inv-sheet-action"
              onClick={() => choose(() => setTransferOpen(true))}
              disabled={nothingOnTheShelf}
              title={nothingOnTheShelf ? "Nothing on the shelf to move" : `Move stock out of ${shop.name}`}
            >
              Transfer stock
            </button>
            {canOperateFlow ? (
              <>
                <button
                  type="button"
                  className="inv-sheet-action"
                  onClick={() => choose(() => setWasteModal({}))}
                  disabled={nothingOnTheShelf}
                  title={nothingOnTheShelf ? "Nothing on the shelf to write off" : undefined}
                >
                  Log waste
                </button>
                <button
                  type="button"
                  className="inv-sheet-action"
                  onClick={() => choose(() => setConsumableOpen(true))}
                >
                  Consumables
                </button>
              </>
            ) : null}
            <button type="button" className="inv-sheet-action" onClick={() => choose(() => setStatsOpen(true))}>
              Statistics
            </button>
            {canOperateFlow ? (
              <button type="button" className="inv-sheet-action" onClick={() => choose(() => setHistoryOpen(true))}>
                History
              </button>
            ) : null}
            {hasRole("OWNER", "MANAGER") ? (
              <Link
                className="inv-sheet-action"
                to={`/app/schedule?locationId=${encodeURIComponent(shop.id)}`}
              >
                Schedule
              </Link>
            ) : null}
            <button type="button" className="inv-sheet-close" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      ) : null}

      {transferOpen ? (
        <InterLocationTransferModal
          fromLocation={shop}
          destLocations={otherShops}
          itemOptionsFromStock={itemOptionsFromStock}
          preset={transferPreset}
          onClose={closeTransfer}
          onSuccess={() => {
            closeTransfer();
            onChanged();
          }}
        />
      ) : null}

      {wasteModal ? (
        <LogWasteModal
          key={wasteModal.presetItemId ?? "pick-item"}
          locationId={shop.id}
          itemOptionsFromStock={itemOptionsFromStock}
          initialItemId={wasteModal.presetItemId}
          initialItemDisplayName={wasteModal.presetItemName}
          onClose={() => setWasteModal(null)}
          onSuccess={() => {
            setWasteModal(null);
            onChanged();
          }}
        />
      ) : null}

      {consumableOpen ? (
        <ConsumablesModal
          location={shop}
          rows={rows}
          onClose={() => setConsumableOpen(false)}
          onInventoryChanged={onChanged}
        />
      ) : null}

      <InventoryStatisticsModal
        scope="location"
        open={statsOpen}
        onClose={() => setStatsOpen(false)}
        locationId={shop.id}
        locationName={shop.name}
      />

      <ReceiveBatchHistoryModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        locationId={shop.id}
        locationName={shop.name}
        onSaved={onChanged}
      />
    </>
  );
}
